/**
 * The analogue dial, on its own.
 *
 * Lifted out of LocationClock unchanged — same 120-unit viewBox, same tick ring, same shield
 * mark, same hand geometry and the same per-face hand colours. LocationClock still owns its
 * card (the name header, the digital readout, the objects/engineers tiles); it just no longer
 * owns the drawing.
 *
 * The reason to extract rather than copy: the map's hover clock has to BE the Location Tracking
 * clock, not a lookalike. Two copies of this geometry drift the first time someone retunes one
 * of them, and the drift is invisible until the two surfaces are seen side by side.
 *
 * SIZE IS PINNED INLINE, deliberately. The map stylesheet carries `svg { width: 100% }` rules
 * for the canvas, and an SVG's `width` ATTRIBUTE loses to any stylesheet selector that matches
 * it — which is how a 10x10 legend swatch once stretched to the full width of the legend. An
 * inline style outranks every selector short of !important, so the dial cannot be stretched by
 * a rule written elsewhere.
 */

export type ClockFace = 'theme' | 'refined-minimalist' | 'premium-blue' | 'frosted-glass' | 'dark-elegant'

const CX = 60
const CY = 60

/** Hand colours per PNG face — the dial art varies light/dark. 'theme' uses CSS tokens. */
const FACE_HANDS: Record<string, { hour: string; minute: string; second: string; dot: string }> = {
    'refined-minimalist': { hour: '#0f172a', minute: '#334155', second: '#dc2626', dot: '#0f172a' },
    'premium-blue': { hour: '#e2e8f0', minute: '#cbd5e1', second: '#38bdf8', dot: '#e2e8f0' },
    'frosted-glass': { hour: '#1e293b', minute: '#334155', second: '#0ea5e9', dot: '#1e293b' },
    'dark-elegant': { hour: '#f1f5f9', minute: '#cbd5e1', second: '#fbbf24', dot: '#f8fafc' },
}

const TICKS = Array.from({ length: 12 }, (_, i) => i * 30)

export interface ClockDialProps {
    hours: number
    minutes: number
    /** Omit or pass null for no seconds hand. */
    seconds?: number | null
    /** Rendered diameter in px. */
    size: number
    face?: ClockFace
    ariaLabel?: string
}

export function ClockDial({ hours, minutes, seconds = null, size, face = 'theme', ariaLabel }: ClockDialProps) {
    const showSeconds = seconds != null
    const sec = seconds ?? 0
    const hourDeg = (hours % 12) * 30 + minutes * 0.5
    const minDeg = minutes * 6 + sec * 0.1
    const secDeg = sec * 6

    const isTheme = face === 'theme'
    const hands = FACE_HANDS[face]
    const handProps = (kind: 'hour' | 'minute' | 'second') => {
        if (isTheme) {
            return { className: kind === 'second' ? 'stroke-primary' : 'stroke-foreground' }
        }
        return { stroke: hands ? hands[kind] : '#0f172a' }
    }

    return (
        <div className="relative shrink-0" style={{ width: size, height: size }}>
            {!isTheme && (
                <img
                    src={`/clock-faces/${face}.png`}
                    alt=""
                    className="absolute inset-0 h-full w-full object-contain"
                    draggable={false}
                />
            )}
            <svg
                viewBox="0 0 120 120"
                className="absolute inset-0"
                style={{ width: size, height: size }}
                role="img"
                aria-label={ariaLabel}
            >
                {isTheme && (
                    <>
                        <circle cx={CX} cy={CY} r="56" className="fill-card stroke-primary/50" strokeWidth="2.5" />
                        {TICKS.map((deg, i) => (
                            <line
                                key={i}
                                x1={CX}
                                y1={8}
                                x2={CX}
                                y2={i % 3 === 0 ? 18 : 13}
                                className={i % 3 === 0 ? 'stroke-primary' : 'stroke-muted-foreground/60'}
                                strokeWidth={i % 3 === 0 ? 3 : 1}
                                strokeLinecap="round"
                                transform={`rotate(${deg} ${CX} ${CY})`}
                            />
                        ))}
                    </>
                )}
                {/* VaultSentinel shield (favicon) — small mark on every face, behind the hands */}
                <image href="/favicon.png" x={CX - 6} y={29} width={12} height={14} preserveAspectRatio="xMidYMid meet" opacity={0.95} />
                <line x1={CX} y1={CY + 8} x2={CX} y2={CY - 30} strokeWidth="3.5" strokeLinecap="round" transform={`rotate(${hourDeg} ${CX} ${CY})`} {...handProps('hour')} />
                <line x1={CX} y1={CY + 10} x2={CX} y2={CY - 44} strokeWidth="2.5" strokeLinecap="round" transform={`rotate(${minDeg} ${CX} ${CY})`} {...handProps('minute')} />
                {showSeconds && (
                    <line x1={CX} y1={CY + 12} x2={CX} y2={CY - 48} strokeWidth="1.5" strokeLinecap="round" transform={`rotate(${secDeg} ${CX} ${CY})`} {...handProps('second')} />
                )}
                <circle cx={CX} cy={CY} r="3.5" {...(isTheme ? { className: 'fill-foreground' } : { fill: hands ? hands.dot : '#0f172a' })} />
            </svg>
        </div>
    )
}

/** The face the engineer chose on Location Tracking, so the map's clock matches it. */
export const CLOCK_SETTINGS_KEY = 'locationTracking.clockSettings'

export function readClockFace(): ClockFace {
    try {
        const saved = localStorage.getItem(CLOCK_SETTINGS_KEY)
        if (!saved) return 'theme'
        const face = JSON.parse(saved)?.face
        return typeof face === 'string' ? (face as ClockFace) : 'theme'
    } catch {
        return 'theme'
    }
}
